import router from '../router';
import store from '../store';
import tokenManager from '../services/tokenManager';
import { notifyHostAuthInvalid } from '../services/embedAuthSignal.mjs';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://api.dale365.com/api/v1';

/**
 * Initialize session validation.
 *
 * We perform a single sanity check on boot (useful when the user returns
 * after closing the tab for hours and the stored token may have been
 * invalidated by a login on another device).
 *
 * We deliberately do NOT poll on an interval. Every authenticated request
 * already goes through `fetchWithAuth`, which handles 401 + refresh. A 30s
 * poll was forcing logouts on any transient network blip (refresh took
 * longer than the timeout, verify-token hit a slow path, etc.).
 */
export const initSessionCheck = () => {
  const checkServerConnection = async () => {
    const userToken = localStorage.getItem('userToken');
    if (!userToken) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/verify-token/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Token ${userToken}`
        }
      });

      // Only act on a definitive "token is dead" signal. Network/5xx errors
      // leave the session alone — authFetch will re-challenge on the next
      // real request if needed.
      if (response.status === 401) {
        // Try refresh first. If refresh also confirms the token is dead,
        // then force logout.
        const refreshed = await tokenManager.refreshToken();
        if (refreshed) {
          return;
        }
        console.warn('Session invalid on boot — token refresh failed');
        forceLogout();
        return;
      }

      if (response.ok) {
        const data = await response.json().catch(() => null);
        if (data && data.valid === false) {
          const refreshed = await tokenManager.refreshToken();
          if (refreshed) {
            return;
          }
          forceLogout();
          return;
        }
        if (data && data.user && store) {
          store.commit('auth/SET_USER', data.user);
        }
      }
      // Any other status (5xx, network error) — leave the user alone.
    } catch (error) {
      // Network failure on boot check is non-fatal. Keep the user logged in.
      console.error('Erro ao verificar sessão:', error);
    }
  };

  const forceLogout = () => {
    // Clear auth-specific data (not all localStorage)
    localStorage.removeItem('userToken');
    localStorage.setItem('userLoggedIn', 'false');

    // Stop token manager auto-refresh
    tokenManager.stop();

    // Dentro do Dale Desktop quem decide o proximo passo e o host. Sem esta
    // guarda, um token trocado por um login na web fazia o painel embutido
    // virar o formulario de login do site (#234 cobriu authFetch e
    // tokenManager; este caminho de boot tinha ficado de fora).
    if (notifyHostAuthInvalid()) return;

    // If store is available, dispatch logout
    if (store) {
      store.dispatch('auth/logout').catch(() => {});
    }

    // Redirect to login
    router.push('/login');
  };

  // One-shot check on init. No interval.
  checkServerConnection();

  // Return null so existing callers that may `clearInterval` on the return
  // value don't blow up.
  return null;
};
